import { useState, useEffect } from 'react';
import type { TimeException } from '../../types/timeCondition';
import { MONTHS } from '../../types/timeCondition';
import { Form, FormField, FormLabel, FormInput, FormSelect, FormCheckbox, Button } from '../ui';

interface ExceptionFormProps {
  exception: TimeException | null;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  isLoading: boolean;
  onClose: () => void;
}

function ExceptionForm({ exception, onSubmit, isLoading, onClose }: ExceptionFormProps) {
  const today = new Date();
  const [exceptionData, setExceptionData] = useState<TimeException>(
    exception || {
      exception_day: today.getDate(),
      exception_month: today.getMonth() + 1,
      exception_year: today.getFullYear(),
      description: 'Feriado',
      is_available: false,
    }
  );

  // Update state when exception prop changes
  useEffect(() => {
    if (exception) {
      setExceptionData(exception);
    }
  }, [exception]);

  const handleChange = (field: keyof TimeException, value: any) => {
    setExceptionData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <Form onSubmit={onSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormField>
          <FormLabel htmlFor="exception_day" required>
            Dia
          </FormLabel>
          <FormInput
            type="number"
            id="exception_day"
            name="exception_day"
            min="1"
            max="31"
            value={exceptionData.exception_day}
            onChange={e => handleChange('exception_day', parseInt(e.target.value))}
            required
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="exception_month" required>
            Mês
          </FormLabel>
          <FormSelect
            id="exception_month"
            name="exception_month"
            value={exceptionData.exception_month}
            onChange={e => handleChange('exception_month', parseInt(e.target.value))}
            required
          >
            {MONTHS.map(month => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="exception_year">Ano (opcional)</FormLabel>
          <FormInput
            type="number"
            id="exception_year"
            name="exception_year"
            min="2000"
            max="2100"
            value={exceptionData.exception_year || ''}
            onChange={e => {
              const value = e.target.value ? parseInt(e.target.value) : null;
              handleChange('exception_year', value);
            }}
            placeholder="Deixe em branco para recorrente"
          />
        </FormField>
      </div>

      <FormField>
        <FormLabel htmlFor="description" required>
          Descrição
        </FormLabel>
        <FormInput
          id="description"
          name="description"
          value={exceptionData.description}
          onChange={e => handleChange('description', e.target.value)}
          required
        />
      </FormField>

      <FormField>
        <div className="flex items-center">
          <FormCheckbox
            id="is_available"
            name="is_available"
            checked={exceptionData.is_available}
            onChange={e => handleChange('is_available', e.target.checked)}
          />
          <FormLabel htmlFor="is_available" className="ml-2 mb-0">
            Disponível nesta data
          </FormLabel>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          Se marcado, os assistentes estarão disponíveis nesta data. Se desmarcado, os assistentes
          não estarão disponíveis.
        </p>
      </FormField>

      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
        <Button type="submit" isLoading={isLoading} className="sm:col-start-2">
          {exception ? 'Salvar' : 'Criar'}
        </Button>
        <Button type="button" variant="secondary" onClick={onClose} className="sm:col-start-1">
          Cancelar
        </Button>
      </div>
    </Form>
  );
}

export default ExceptionForm;
